import React from 'react'
import { Table, Badge, Button, Dropdown } from 'react-bootstrap'
import { UserWithRole } from '../../../domain/usecases/UserRoles'
import { Role } from '../../../domain/usecases/Roles'

interface UsersListProps {
  users: UserWithRole[]
  roles: Role[]
  onEdit?: (user: UserWithRole) => void
  onRemove?: (userId: string) => void
}

export const UsersList: React.FC<UsersListProps> = ({
  users,
  roles,
  onEdit,
  onRemove
}) => {
  const getRoleName = (roleId: string, isSystemRole: boolean) => {
    const role = roles.find((r) => r.id === roleId)
    return role ? role.display_name : 'Função Desconhecida'
  }

  const getScopeTypeBadge = (scopeType: string) => {
    if (scopeType === 'full') {
      return <Badge variant="success">Acesso Completo</Badge>
    }
    return <Badge variant="warning">Acesso Restrito</Badge>
  }

  const getStatusBadge = (status: string) => {
    if (status === 'active') {
      return <Badge variant="success">Ativo</Badge>
    }
    if (status === 'pending') {
      return <Badge variant="warning">Convite Pendente</Badge>
    }
    return <Badge variant="secondary">Inativo</Badge>
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-5">
        <i
          className="feather icon-users"
          style={{ fontSize: '48px', color: '#ccc' }}
        />
        <p className="mt-3 text-muted">Nenhum usuário encontrado</p>
      </div>
    )
  }

  return (
    <Table responsive hover>
      <thead>
        <tr>
          <th>Nome</th>
          <th>E-mail</th>
          <th>Função</th>
          <th>Escopo de Acesso</th>
          <th>Status</th>
          <th className="text-right">Ações</th>
        </tr>
      </thead>
      <tbody>
        {users.map((user) => (
          <tr key={user.user_id}>
            <td>
              <strong>{user.user_name}</strong>
            </td>
            <td>{user.user_email}</td>
            <td>
              {getRoleName(user.role_id, user.is_system_role)}
              {user.is_system_role && (
                <Badge variant="info" className="ml-2">
                  Sistema
                </Badge>
              )}
            </td>
            <td>
              {getScopeTypeBadge(user.scope_type)}
              {user.scope_type === 'restricted' && user.scope_resources && (
                <div className="mt-1">
                  <small className="text-muted">
                    {user.scope_resources.length} SPE(s)
                  </small>
                </div>
              )}
            </td>
            <td>{getStatusBadge(user.status)}</td>
            <td className="text-right">
              {(onEdit || onRemove) && (
                <Dropdown alignRight>
                  <Dropdown.Toggle
                    variant="link"
                    className="btn-sm text-muted"
                    id={`user-actions-${user.user_id}`}
                  >
                    <i className="feather icon-more-vertical" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {onEdit && user.user_id && (
                      <Dropdown.Item onClick={() => onEdit(user)}>
                        <i className="feather icon-edit mr-2" />
                        Editar Função
                      </Dropdown.Item>
                    )}
                    {!user.user_id && (
                      <Dropdown.Item disabled>
                        <i className="feather icon-clock mr-2" />
                        Aguardando aceite do convite
                      </Dropdown.Item>
                    )}
                    {onEdit && onRemove && user.user_id && <Dropdown.Divider />}
                    {onRemove && (
                      <Dropdown.Item
                        // convite pendente ainda não tem user_id: remove pela linha do convite
                        onClick={() => onRemove(user.user_id || user.id || '')}
                        className="text-danger"
                      >
                        <i className="feather icon-trash-2 mr-2" />
                        Remover da Empresa
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}
